import axios, {
  type AxiosRequestConfig,
  type AxiosRequestHeaders,
  type Method,
} from 'axios';
import i18n, { type SupportedLanguage } from '@/locales';

export type RequestParams<TData = unknown> = Omit<
  AxiosRequestConfig<TData>,
  'url' | 'method' | 'data' | 'params' | 'headers'
> & {
  url: string;
  method?: Method;
  data?: TData;
  headers?: AxiosRequestConfig<TData>['headers'];
};

const API_UAT = 'https://api.test.jdx.xyb2b.cn';
const API_PRODUCTION = 'https://api.bilionpass.biz';

const localeMap: Record<SupportedLanguage, string> = {
  'zh-CN': 'zh_CN',
  'en-US': 'en_US',
  'ja-JP': 'ja_JP',
};

function resolveBaseURL(): string {
  const configuredBaseURL = import.meta.env.FARM_API_BASE_URL as string | undefined;
  if (configuredBaseURL) return configuredBaseURL;

  if (import.meta.env.DEV) return '';

  return import.meta.env.FARM_DEPLOY_ENV === 'test' ? API_UAT : API_PRODUCTION;
}

function resolveRequestLocale(): string {
  const language = (i18n.resolvedLanguage || i18n.language || 'zh-CN') as SupportedLanguage;
  return localeMap[language] ?? localeMap['zh-CN'];
}

const axiosInstance = axios.create({ baseURL: resolveBaseURL() });

axiosInstance.interceptors.request.use(config => {
  config.headers = (config.headers || {}) as AxiosRequestHeaders;
  config.headers.set('locale', resolveRequestLocale());
  return config;
});

function buildConfig<TData>(
  params: RequestParams<TData>,
  forceMethod?: Method,
): AxiosRequestConfig<TData> {
  const { url, method = 'get', data, headers, ...rest } = params;
  const httpMethod = (forceMethod || method).toLowerCase() as Method;
  const isGet = httpMethod === 'get';

  return {
    ...rest,
    url,
    method: httpMethod,
    params: isGet ? data : undefined,
    data: isGet ? undefined : data,
    headers,
  };
}

function send<TResponse, TData = unknown>(
  params: RequestParams<TData>,
  forceMethod?: Method,
): Promise<TResponse> {
  return axiosInstance
    .request<TResponse>(buildConfig(params, forceMethod))
    .then(response => response.data);
}

const request = {
  config: { prefix: resolveBaseURL() },
  fetch: <TResponse, TData = unknown>(params: RequestParams<TData>) =>
    send<TResponse, TData>(params),
  get: <TResponse, TData = unknown>(params: RequestParams<TData>) =>
    send<TResponse, TData>(params, 'get'),
  post: <TResponse, TData = unknown>(params: RequestParams<TData>) =>
    send<TResponse, TData>(params, 'post'),
  put: <TResponse, TData = unknown>(params: RequestParams<TData>) =>
    send<TResponse, TData>(params, 'put'),
  delete: <TResponse, TData = unknown>(params: RequestParams<TData>) =>
    send<TResponse, TData>(params, 'delete'),
  patch: <TResponse, TData = unknown>(params: RequestParams<TData>) =>
    send<TResponse, TData>(params, 'patch'),
};

export default request;
